import { Link, graphql, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import React from "react"

import Layout from "@components/Layout/Layout"
import ProductHero from "@components/ProductHero/ProductHero"

import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/ui/table"

import AllPrecos from "@components/AllPrecos/AllPrecos"
import Button from "@components/Button/Button"
import ContactUs from "@components/ContactUs/contactus"
import DocumentLink from "@components/DocumentLink/DocumentLink"
import Environment from "@components/Environment/Environment"
import Hotspots from "@components/Hotspots/Hotspots"
import PrecoThermiqueImageGallery from "@components/ImageGallery/PrecoThermiqueImageGallery"
import { ArrowRight } from "lucide-react"

// Data Imports
import { walls } from "@components/AllPrecos/AllPrecos"

const ThermalTemplate = ({ lang, texts }) => {
  const data = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          lang
        }
      }
      schema_principe: file(
        name: { eq: "schema_principe_thermique" }
        sourceInstanceName: { eq: "images" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 100
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      schema_principe_de: file(
        name: { eq: "schema_principe_thermowand" }
        sourceInstanceName: { eq: "images" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 100
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      schema_principe_en: file(
        name: { eq: "schema_principe_thermalwall" }
        sourceInstanceName: { eq: "images" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 100
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      product: file(
        name: { eq: "premur_thermique_3D" }
        sourceInstanceName: { eq: "images" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 100
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      principe_pose: file(
        name: { eq: "principe pose TH" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      nancy_grand_coeur_2: file(
        name: { eq: "Logements Nancy Grand Coeur 2" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      nancy_grand_coeur: file(
        name: { eq: "Logements Nancy Grand Coeur" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
    }
  `)
  console.log(data)
  return (
    <Layout lang={lang} dropdownId="thermal">
      <ProductHero>
        <div className="flex max-w-full basis-1/3 flex-col justify-between gap-y-5 p-4 lg:p-8">
          <div className="mt-24 flex flex-col gap-y-4">
            <h1>{texts.title}</h1>
            {texts.subtitle && <h2 className="text-2xl">{texts.subtitle}</h2>}
            <div
              className="[&_li]:list-disc [&_ul]:mt-5 [&_ul]:list-inside"
              dangerouslySetInnerHTML={{
                __html: texts.second_hero.copy,
              }}
            />
          </div>
          <div className="flex flex-col">
            {texts.cta.map((it, i) => (
              <Button
                key={i}
                buttonString={it.title}
                buttonURL={it.slug}
                {...(i === 0 && { black: true })}
              />
            ))}
          </div>
        </div>
        <div className="grid basis-2/3 grid-cols-2 grid-rows-2">
          <div className="col-span-1 row-span-1 bg-gray-100 p-10">
            <GatsbyImage
              objectFit="contain"
              image={getImage(data.product)!}
              alt={texts.seo_title}
              className="h-full"
            />
          </div>
          <GatsbyImage
            image={getImage(data.nancy_grand_coeur_2)!}
            alt={texts.seo_title}
            className="col-start-2 row-span-1"
          />
          <GatsbyImage
            image={getImage(data.nancy_grand_coeur)!}
            alt={texts.seo_title}
            className="col-span-full row-start-2"
          />
        </div>
      </ProductHero>

      {/* Technical data table */}
      <div id="main" className="mb-7 flex flex-col lg:mb-0 lg:flex-row">
        <div className="basis-1/3 px-5 py-20 lg:border-t-[1px] lg:border-t-gray-200">
          <h2 className="mb-3 uppercase">{texts.variante.title[lang]}</h2>
          <div className="flex flex-col gap-y-2">
            {walls[lang].products.map((variant, i) => (
              <Link key={i} to={variant.url} className="text-black underline">
                {variant.name}
              </Link>
            ))}
          </div>
        </div>
        <div className="basis-2/3 py-10 lg:py-16">
          <div className="mx-auto max-w-3xl space-y-7">
            <Table>
              <TableHeader>
                <TableRow className="border-none">
                  <TableHead className="py-2 text-2xl font-bold uppercase text-black">
                    {texts.product_table.title}
                    {texts.product_table.subtitle && (
                      <span className="block text-xs font-thin">
                        {texts.product_table.subtitle}
                      </span>
                    )}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="flex flex-col justify-stretch gap-y-2">
                {Object.entries(texts.product_table.table).map(
                  (td: any, i: number) => (
                    <TableRow className="flex gap-x-2 border-none text-base font-semibold">
                      <TableCell className="flex-1 bg-fehr-500 p-2 text-white">
                        {td[0]}
                      </TableCell>
                      <TableCell className="flex-1 bg-fehr-500/25 p-2">
                        {td[1]}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
              {texts.product_table.asterix && (
                <TableCaption>{texts.product_table.asterix}</TableCaption>
              )}
            </Table>
          </div>
        </div>
      </div>

      <section className="mx-auto max-w-4xl space-y-5 px-4 py-10 sm:px-6 lg:px-8">
        {texts.summary.map((paragraph: string, i: number) => (
          <p key={i}>{paragraph}</p>
        ))}
      </section>

      {/* HOSPOTS */}
      <Hotspots hotspots={texts.hotspots} imageData={getImage(data.product)} />

      {/* Applications */}
      <section className="flex max-lg:flex-col">
        <div className="basis-1/3 bg-slate-100 p-12">
          <h2 className="uppercase">{texts.applications.title}</h2>
          <ArrowRight className="size-10 rotate-90 stroke-fehr-500 lg:rotate-0" />
        </div>
        <div className="grid flex-auto basis-2/3 grid-cols-2 lg:grid-cols-5">
          {texts?.applications?.applications?.length > 0 &&
            texts.applications.applications.map((application, i) => (
              <div
                key={i}
                className="flex flex-col items-center justify-center p-3 lg:even:bg-slate-50"
              >
                <dt className="inline font-semibold text-gray-900">
                  <application.icon
                    className="size-32 text-indigo-600 lg:size-14"
                    aria-hidden="true"
                  />
                </dt>{" "}
                <dd className="text-center text-base lg:text-xs">
                  {application.name}
                </dd>
              </div>
            ))}
        </div>
      </section>

      {/* Avantages thermique */}

      {/* {texts[lang]?.avantages_thermal.items.length > 0 && (
          <section>
            <h2>{texts[lang].avantages_thermal.title}</h2>
            <div className="grid">
              {texts[lang].avantages_thermal.items.map((item) => (
                <p className="cards">{item}</p>
              ))}
            </div>
          </section>
        )} */}

      {/* Environnement */}
      <Environment lang={lang} className="my-7 lg:my-16" />

      {/* Download Section */}
      {texts.download_section && (
        <section
          id="download"
          className="mx-auto my-20 max-w-7xl px-3 sm:px-6 lg:my-32 lg:px-8"
        >
          <h2 className="mb-16 text-center uppercase">
            {texts.download_section.title}
          </h2>
          <div>
            {texts.download_section.sections.map((section, i) => (
              <div key={i} className="mb-20">
                <h3 className="mb-8 p-2 font-extralight uppercase lg:p-7">
                  {section.title}
                </h3>
                {section.sub_section.map((sub, i) => (
                  <div key={i} className="mb-10 text-sm">
                    {sub.title && (
                      <h4 className="mb-8 text-sm font-bold uppercase">
                        {sub.title}
                      </h4>
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-3">
                      {sub.link.map((li, i) => (
                        <DocumentLink
                          key={i}
                          text={li[0]}
                          slug={`${process.env.GATSBY_DOCUMENTATION_URL}/${li[1]}`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="flex flex-col gap-y-16">
        <PrecoThermiqueImageGallery lang={lang} />
        <AllPrecos lang={lang} />
      </div>
      <ContactUs lang={lang} />
    </Layout>
  )
}

export default ThermalTemplate
